<script lang="ts">
import type { MaybeComputedElementRef } from "@vueuse/core";
import type { VariantProps } from "class-variance-authority";
import type { RouteLocationRaw } from "vue-router";
import { cva } from "class-variance-authority";
import { useElementAutoFocus } from "ls/composables/useElementAutos";
import { useForwardExpose } from "reka-ui";
import { isObject } from "@/common/lib/guards";

export interface SimpleButtonProps {
  autofocus?: boolean;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  disabled?: boolean;
  active?: boolean;
  href?: RouteLocationRaw | string;
};

export type ButtonProps = VariantProps<typeof button>;

export const button = cva([
  "inline-flex items-center justify-center gap-x-2 rounded-sm leading-6 tracking-btn whitespace-nowrap outline-offset-2 transition-btn focus-visible:outline-1 data-disabled:pointer-events-none data-disabled:opacity-50",
], {
  variants: {
    variant: {
      primary: [
        "font-medium text-white",
        "border border-transparent",
        "bg-blue-600 group-data-[state=open]:bg-blue-800 data-[state=open]:bg-blue-800 dark:bg-blue-400 dark:data-[state=open]:bg-blue-500",
        "hover:bg-blue-800 dark:hover:bg-blue-500",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
        "data-active:bg-sky-100 data-active:text-blue-600 data-active:hover:bg-sky-200 dark:data-active:bg-sky-200 dark:data-active:text-blue-400 dark:data-active:hover:bg-sky-100",
      ],
      secondary: [
        "font-medium dark:text-slate-100",
        "data-active:text-sky-400 dark:data-active:text-blue-300",
        "border border-slate-300 dark:border-zinc-600",
        "bg-white group-data-[state=open]:bg-gray-100 data-[state=open]:bg-gray-100 dark:bg-zinc-900 dark:data-[state=open]:bg-gray-600",
        "hover:bg-gray-100 dark:hover:bg-gray-600",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
      ],
      destructive: [
        "font-medium text-white",
        "border border-transparent",
        "bg-red-600 data-[state=open]:bg-red-700 dark:data-[state=open]:bg-red-500",
        "hover:bg-red-700 dark:hover:bg-red-500",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
      ],
      ghost: [
        "font-normal data-active:text-blue-600 dark:text-slate-100 dark:data-active:text-blue-300",
        "data-[state=open]:bg-gray-200/40 dark:data-[state=open]:bg-gray-700/40",
        "hover:bg-gray-200/40 dark:hover:bg-gray-700/40",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
      ],
      link: [
        "border-0 bg-transparent font-normal text-blue-600 select-auto hover:underline focus-visible:underline",
        "data-[state=open]:text-blue-600",
        "hover:text-blue-600",
        "focus-visible:outline-blue-600",
      ],
      "link-dark": [
        "border-0 bg-transparent font-normal text-black select-auto hover:underline focus-visible:underline",
        "data-[state=open]:text-black",
        "hover:text-black",
        "focus-visible:outline-black",
      ],
      "link-light": [
        "border-0 bg-transparent font-normal text-blue-300 select-auto hover:underline focus-visible:underline",
        "data-[state=open]:text-blue-300",
        "hover:text-blue-300",
        "focus-visible:outline-blue-300",
      ],
      purple: [
        "font-medium text-white",
        "border border-transparent",
        "bg-purple-600 data-[state=open]:bg-purple-800 dark:bg-purple-400 dark:data-[state=open]:bg-purple-500",
        "hover:bg-purple-800 dark:hover:bg-purple-500",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
      ],
      stealth: [
        "font-medium text-slate-400 dark:text-slate-100",
        "data-active:text-sky-400 dark:data-active:text-blue-300",
        "border-transparent dark:border-zinc-600",
        "bg-transparent group-data-[state=open]:bg-gray-100 data-[state=open]:bg-gray-100 dark:bg-zinc-900 dark:data-[state=open]:bg-gray-700",
        "group-data-[state=open]:text-inherit hover:border-slate-300 hover:bg-gray-100 hover:text-inherit data-[state=open]:text-inherit dark:hover:bg-gray-600",
        "focus-visible:outline-indigo-600 dark:focus-visible:outline-slate-100",
      ],
    },
    size: {
      small: "h-5 px-2 text-xs",
      medium: "h-7 px-2",
      large: "h-8 px-3.5",
      icon: "size-7 flex-none",
      "icon-small": "size-4 flex-none",
      auto: "",
    },
  },
  compoundVariants: [
    {
      size: ["icon", "icon-small"],
      variant: "ghost",
      class: "text-slate-600 hover:text-slate-800 focus-visible:text-slate-800 dark:hover:text-slate-100 dark:focus-visible:text-slate-100",
    },
  ],
  defaultVariants: {
    variant: "primary",
    size: "medium",
  },
});
</script>

<script setup lang="ts">
const props = defineProps<SimpleButtonProps>();

const { currentRef, forwardRef } = useForwardExpose();

useElementAutoFocus(currentRef as MaybeComputedElementRef<HTMLElement>, {
  enabled: () => props.autofocus,
});
</script>

<template>
  <RouterLink
    v-if="isObject(href)"
    :ref="forwardRef"
    :to="href"
    :data-active="active ? '' : undefined"
    :data-disabled="disabled ? '' : undefined"
    :class="button({ variant, size })"
  >
    <slot />
  </RouterLink>
  <a
    v-else-if="href"
    :ref="forwardRef"
    :href="href"
    :data-active="active ? '' : undefined"
    :data-disabled="disabled ? '' : undefined"
    :class="button({ variant, size })"
  >
    <slot />
  </a>
  <button
    v-else
    :ref="forwardRef"
    type="button"
    :disabled="disabled"
    :data-active="active ? '' : undefined"
    :data-disabled="disabled ? '' : undefined"
    :class="button({ variant, size })"
  >
    <slot />
  </button>
</template>
